import {type IconName, TagGroup} from '@wandb/components';
import React from 'react';

import {Tooltip} from '../../../weave/components/Tooltip';
import type {ComposerReferencePart} from '../composer/slate';

export interface ReferenceTagProps {
  icon: IconName;
  text: string;
  tooltip?: {children: React.ReactNode};
  onRemove?: () => void;
}

/** Shared inline tag for structured references rendered in user messages. */
export const ReferenceTag = ({
  icon,
  text,
  tooltip,
  onRemove,
}: ReferenceTagProps) => {
  const tag = (
    <span className="mr-4 inline-flex max-w-full align-middle [&_[role=grid]]:min-w-0 [&_[role=grid]]:w-auto [&_[role=row]]:![border-radius:var(--border-radius-sm)]">
      <TagGroup
        aria-label={text}
        color="blue"
        wrap={false}
        items={[
          {
            id: 'reference',
            icon,
            textValue: text,
            cssTruncate: true,
            removable: onRemove != null,
          },
        ]}
        onRemove={onRemove == null ? undefined : onRemove}
      />
    </span>
  );

  return tooltip == null ? (
    tag
  ) : (
    <Tooltip content={tooltip.children} noTriggerWrap trigger={tag} />
  );
};

export const ComposerReferenceTag = ({
  part,
  onRemove,
}: {
  part: ComposerReferencePart;
  onRemove?: () => void;
}) => {
  const {icon, text} = referencePresentation(part);
  return <ReferenceTag icon={icon} text={text} onRemove={onRemove} />;
};

function referencePresentation(part: ComposerReferencePart): {
  icon: IconName;
  text: string;
} {
  switch (part.type) {
    case 'wandb-run':
      return {
        icon: 'running-repeat',
        text: part.runDisplayName ?? part.runName,
      };
    case 'wandb-workspace-panel':
      return {
        icon: 'panel',
        text: part.queryPanelTableKey ?? part.title ?? part.panelType,
      };
    case 'wandb-call':
      return {
        icon: part.callDisplayName?.toLowerCase().startsWith('eval')
          ? 'baseline-alt'
          : 'layout-tabs',
        text: part.callDisplayName ?? `Call ${part.callId.slice(0, 8)}`,
      };
  }
}
